import { Resource } from '@nextforce/api'
import { Icon, makeStyles } from '@rneui/themed'
import { Image } from 'expo-image'
import { isVideoType } from 'file_folder_repository/util'
import { FileFolderIcon } from 'file_folder_repository/views/file_folder_icon'
import { Platform, useWindowDimensions, View } from 'react-native'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { WEB_SCREEN_WIDTH } from 'shared/constants'
import { getDarkShadeColor, getLightShadeColor } from 'shared/utils'
import { FolderListItem } from './list_item'

export const ITEM_MARGIN = 4
export const ROW_ITEMS = 4
export const ROW_PADDING = 2

export function ChatGroupDocumentGridItemRow({
  items: items,
  onPress,
  onLongPress,
}: {
  items: Resource[]
  onPress(resource: Resource): void
  onLongPress(resource: Resource): void
}) {
  const styles = useStyles()
  return (
    <View style={styles.row}>
      {items.map(file => (
        <ChatGroupDocumentGridItem
          key={file.id}
          item={file}
          onPress={onPress}
          onLongPress={onLongPress}
        />
      ))}
    </View>
  )
}

export function ChatGroupDocumentGridItem({
  item,
  onPress,
  onLongPress,
}: {
  item: Resource
  onPress(item: Resource): void
  onLongPress(item: Resource): void
}) {
  const styles = useStyles()
  const { width } = useWindowDimensions()

  const itemWidth =
    ((Platform.OS === 'web' && width > WEB_SCREEN_WIDTH
      ? WEB_SCREEN_WIDTH
      : width) -
      ROW_PADDING * 2 -
      ITEM_MARGIN * ROW_ITEMS) /
    ROW_ITEMS

  // Determine the width of the item
  const itemStyle =
    item.type === 'folder'
      ? { width: itemWidth * 2 + ITEM_MARGIN, height: itemWidth }
      : { width: itemWidth, height: itemWidth }

  return (
    <PressableOpacity
      onPress={() => onPress(item)}
      onLongPress={() => onLongPress(item)}
      onContextMenu={e => {
        e.preventDefault()
        onLongPress(item)
      }}
    >
      {item.type === 'file' && item.currentVersion?.thumbnailUrl ? (
        <View style={[styles.itemContainer, itemStyle]}>
          <Image
            draggable={false}
            source={{ uri: item.currentVersion.thumbnailUrl }}
            contentFit="cover"
            style={{
              width: itemStyle.width,
              height: itemStyle.height,
              borderRadius: styles.itemContainer.borderRadius,
            }}
          />
          {isVideoType(item.currentVersion.fileMimeType) ? (
            <View style={styles.videoPlayIconContainer}>
              <Image
                draggable={false}
                style={styles.videoPlayIcon}
                source={require('assets/chat/play.png')}
                contentFit="contain"
              />
            </View>
          ) : null}
          {item.isPinned ? (
            <Icon
              name="thumbtack"
              type="font-awesome-5"
              size={15}
              color={styles.pinIcon.color}
              containerStyle={styles.pinIconContainer}
              solid
            />
          ) : null}
        </View>
      ) : item.type === 'folder' ? (
        <FolderListItem
          folder={item}
          onPress={onPress}
          onLongPress={onLongPress}
          style={[styles.itemContainer, itemStyle]}
        />
      ) : (
        <View key={item.id} style={[styles.itemContainer, itemStyle]}>
          <FileFolderIcon resource={item} size={50} />
        </View>
      )}
    </PressableOpacity>
  )
}

const useStyles = makeStyles(theme => ({
  row: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: ITEM_MARGIN,
    alignItems: 'center',
    paddingHorizontal: ROW_PADDING,
  },
  itemContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    backgroundColor:
      theme.mode === 'dark' ? getLightShadeColor(0.1) : getDarkShadeColor(0.05),
    borderRadius: 5,
    marginHorizontal: ITEM_MARGIN / 2,
    padding: 10,
    marginVertical: 0,
  },
  itemText: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.black,
  },
  videoPlayIcon: { height: 25, width: 25 },
  videoPlayIconContainer: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  pinIcon: {
    color: theme.colors.primary,
  },
  pinIconContainer: { position: 'absolute', top: 5, right: 5 },
}))
